import { FC, PropsWithChildren } from 'react';
import { Modal } from 'antd';
import cn from 'classnames';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

type ModalContainerV3Props = PropsWithChildren & {
  title: string;
  showBackBtn?: boolean;
  onClose?: () => void;
};

export const ModalContainerV3: FC<ModalContainerV3Props> = ({
  title,
  showBackBtn,
  onClose,
  children,
}) => {
  const { t } = useTranslation('common');
  const modals = useModalsStore();

  return (
    <ModalStyled
      open={true}
      onCancel={() => {
        modals.closeModal();
        onClose?.();
      }}
      onClose={() => {
        modals.closeModal();
        onClose?.();
      }}
      footer={null}
      title={title}
      className={cn({ hasBackButton: showBackBtn })}
      centered
    >
      {showBackBtn && (
        <BackBtn
          onClick={() => {
            modals.closeModal();
          }}
          type="text"
          icon={<Icon name="arrow-left" />}
        >
          {t('back')}
        </BackBtn>
      )}
      {children}
    </ModalStyled>
  );
};

const ModalStyled = styled(Modal)`
  && {
    width: calc(100vw - 100px) !important;
    height: calc(100vh - 100px) !important;

    .ant-modal-close {
      color: ${COLORS.systemDark200};
      background: ${COLORS.systemLight200};
      border-radius: 50%;
      width: 24px;
      height: 24px;

      &:hover {
        color: ${COLORS.systemDark200};
        background: ${COLORS.systemLight300};
      }
    }

    .ant-modal-title {
      color: ${COLORS.white};
      padding-bottom: 16px;
    }

    .ant-modal-body {
      display: flex;
      gap: 24px;
      height: calc(100vh - 100px - 40px - 48px);
    }

    .ant-modal-content {
      height: 100%;
      min-height: calc(100vh - 100px);
      background: ${COLORS.colorFilePreviewBg};
    }

    .ant-modal-header {
      background: ${COLORS.colorFilePreviewBg};
    }
  }
  ${media.iwMobileOrLandscape} {
    &&& {
      width: 100vw !important;
      height: 100vh !important;
      max-height: unset;
      max-width: unset;
      margin: 0;

      .ant-modal-content {
        height: 100vh;
        border-radius: 0;
        overflow: hidden;
      }

      .ant-modal-header {
        z-index: 1;
        background: none;
        position: relative;
        padding-top: 0;
      }

      .ant-modal-close {
        display: none;
      }

      .ant-modal-content {
        padding: 16px;
      }

      .ant-modal-body {
        height: calc(100vh - 48px - 16px - 48px);
      }

      &.hasBackButton .ant-modal-header {
        padding-top: 34px;
      }
    }
  }
`;

const BackBtn = styled(ButtonIw)`
  && {
    display: none;

    ${media.iwMobileOrLandscape} {
      display: flex;
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      color: ${COLORS.white};
      padding: 16px 0;
      left: 16px;
      height: fit-content !important;

      &&:hover {
        color: ${COLORS.white};
      }
    }
  }
`;
